import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useAnalyticsPageView } from '@chilecompra/react-kit/analytics'
import { Autocomplete, Button, Toast, useAutocomplete, useViewport } from '@chilecompra/react-kit/components'
import { useLanguageFlags } from '@chilecompra/react-kit/i18n'
import {
  onCleanGuestBusinessThunk,
  onCreateTemporarySellerAreasThunk,
  onGetGuestBusinessAreasThunk
} from './RegisterWizard.actions'
import {
  Root,
  DesktopNavigation,
  MobileNavigation,
  Section,
  Title,
  Instructions,
  Subtitle,
  SelectedAreas,
  SelectedArea,
  SelectedAreaText,
  SelectedAreaRemove,
  SelectedAreaRemoveIcon
} from './RegisterWizardBusinessAreas.styles'

/**
 * The RegisterWizardBusinessAreas' container.
 */
const RegisterWizardBusinessAreas = props => {
  const { onChangeStep } = props
  const dispatch = useDispatch()
  const { hasLanguage } = useLanguageFlags()
  const { business, errors, guest, loading } = useSelector(state => state.registerWizard)
  const [selectedAreas, setSelectedAreas] = useState([])
  const { size } = useViewport()

  const handleAreaAdd = updatedArea => {
    if (!updatedArea || typeof updatedArea === 'string') {
      return
    }

    setSelectedAreas(currentAreas => {
      const found = currentAreas.find(area => area.value === updatedArea?.value)

      if (found) {
        return currentAreas
      }

      return currentAreas.concat(updatedArea)
    })
  }
  const handleAreaRemove = updatedArea => () =>
    setSelectedAreas(currentAreas => currentAreas.filter(area => area.value !== updatedArea.value))
  const handleQueryChange = updatedValue => {
    const found = selectedAreas.find(area => area.name === updatedValue)

    if (!updatedValue || found || updatedValue?.length < 3) {
      return
    }

    dispatch(onGetGuestBusinessAreasThunk({ query: updatedValue }))
  }

  const {
    option,
    value,
    onChange: handleChange,
    onSelect: handleSelect
  } = useAutocomplete({
    clearAfterSelect: true,
    changeCallback: handleQueryChange,
    selectCallback: handleAreaAdd
  })
  const handlePrev = () => {
    if (hasLanguage) {
      return onChangeStep('business-info')
    }

    dispatch(
      onCleanGuestBusinessThunk({
        callback: () => onChangeStep('business-search')
      })
    )
  }

  const handleNext = useCallback(
    () =>
      dispatch(
        onCreateTemporarySellerAreasThunk({
          areas: selectedAreas,
          bypassStep: selectedAreas.length === 0 && hasLanguage,
          callback: () => (hasLanguage ? onChangeStep('complete') : onChangeStep('considerations'))
        })
      ),
    [dispatch, onCreateTemporarySellerAreasThunk, selectedAreas, hasLanguage, onChangeStep]
  )

  const disabledNext = (selectedAreas.length === 0 && !hasLanguage) || loading
  const omitStep = selectedAreas.length === 0 && hasLanguage

  useAnalyticsPageView(`/paso/${guest.isForeign ? '5' : '4'}/rubros`)

  return (
    <Root>
      <Section item>
        <Title>Ingresa los rubros en los que vendes productos o servicios</Title>
      </Section>
      <Section item margin="23px 0 33px" maxWidth="898px">
        <Instructions>
          {hasLanguage && 'Con esto podremos mantenerte informado sobre las licitaciones en las que puedes participar.'}
          {!hasLanguage &&
            'Con esto podremos mantenerte informado sobre las licitaciones en las que puedes participar. Agrega por lo menos un rubro para continuar, luego podrás editarlos desde tu escritorio.'}
        </Instructions>
      </Section>
      <Section item maxWidth="551px">
        <Autocomplete
          label="busca tu rubro"
          option={option}
          options={business.areas || []}
          value={value}
          waiting={business.loading}
          onChange={handleChange}
          onSelect={handleSelect}
        />
      </Section>
      <Section item margin="11px 0 0">
        <Subtitle>Rubros seleccionados</Subtitle>
      </Section>
      <Section item margin="13px 0 0">
        <SelectedAreas>
          {selectedAreas.map(area => (
            <SelectedArea key={area.value}>
              <SelectedAreaText>{area.name}</SelectedAreaText>
              <SelectedAreaRemove>
                <SelectedAreaRemoveIcon onClick={handleAreaRemove(area)} />
              </SelectedAreaRemove>
            </SelectedArea>
          ))}
        </SelectedAreas>
      </Section>
      {errors.businessSearchError === 'SOMETHING_WENT_WRONG_ERROR' && (
        <Section item margin="32px 0 0" maxWidth="551px">
          <Toast severity="warning" title="Problema al avanzar">
            <br />
            Lo sentimos, estamos presentando problemas para avanzar al siguiente paso. <br /> Por favor espera unos
            minutos e inténtalo nuevamente.
          </Toast>
        </Section>
      )}
      {!size.isExtraTiny && (
        <Section container margin="52px 0 0 0">
          <DesktopNavigation>
            <Button type="button" variant="text" width="265px" onClick={handlePrev}>
              Atrás
            </Button>
            <Button
              color="primary"
              disabled={disabledNext}
              loading={loading}
              margin="0 0 0 30px"
              type="button"
              width="265px"
              onClick={handleNext}
            >
              {omitStep ? 'Omitir' : 'Siguiente'}
            </Button>
          </DesktopNavigation>
        </Section>
      )}
      {size.isExtraTiny && (
        <MobileNavigation>
          <Button type="button" variant="text" width="168px" onClick={handlePrev}>
            Atrás
          </Button>
          <Button
            color="primary"
            disabled={disabledNext}
            loading={loading}
            type="button"
            width="168px"
            onClick={handleNext}
          >
            {omitStep ? 'Omitir' : 'Siguiente'}
          </Button>
        </MobileNavigation>
      )}
    </Root>
  )
}

RegisterWizardBusinessAreas.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardBusinessAreas.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardBusinessAreas
